import React, { useState } from "react";
import {
  DemoResponsiveGrid,
  DemoSlider,
  DemoWorkbenchCard,
  DemoWorkbenchInset,
  DemoWorkbenchMetricCard,
  DemoWorkbenchNotice,
  DemoWorkbenchStack,
} from "@csk/demo-ui";

type Footprint = [number, number, number, number];

const SegalVerificationPanel = (): React.JSX.Element => {
  const [nSystems, setNSystems] = useState(5);

  // Generate sample footprints
  const footprints: Footprint[] = Array.from({ length: nSystems }, (_, i) => {
    const fp: Footprint = [0, 0, 0, 0];
    const idx1 = (i % 4) as 0 | 1 | 2 | 3;
    fp[idx1] = i + 1;
    if (i > 2) {
      const idx2 = ((i + 1) % 4) as 0 | 1 | 2 | 3;
      fp[idx2] = 1;
    }
    return fp;
  });

  const kappa = (v: Footprint): number =>
    2 *
    (v[0] * v[1] +
      v[0] * v[2] +
      v[0] * v[3] +
      v[1] * v[2] +
      v[1] * v[3] +
      v[2] * v[3]);

  const mergeFootprints = (a: Footprint, b: Footprint): Footprint => [
    a[0] + b[0],
    a[1] + b[1],
    a[2] + b[2],
    a[3] + b[3],
  ];

  // Compute pairwise budgets
  const pairs: { i: number; j: number; B: number }[] = [];
  for (let i = 0; i < nSystems; i++) {
    for (let j = i + 1; j < nSystems; j++) {
      const fpi = footprints[i];
      const fpj = footprints[j];
      const merged = mergeFootprints(fpi, fpj);
      const B = kappa(merged) - kappa(fpi) - kappa(fpj);
      pairs.push({ i, j, B });
    }
  }

  const pairwiseSum = pairs.reduce((s, p) => s + p.B, 0);
  const combined: Footprint = footprints.reduce<Footprint>(
    (acc, fp) => mergeFootprints(acc, fp),
    [0, 0, 0, 0],
  );
  const totalInteraction =
    kappa(combined) - footprints.reduce((s, fp) => s + kappa(fp), 0);
  const segalHolds = totalInteraction === pairwiseSum;
  const bruteForce = Math.pow(2, nSystems) - 1 - pairs.length - nSystems;

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard title="Segal Pairwise Verification (§15.9)">
        <DemoSlider
          id="segal-system-count"
          label="Systems"
          min={2}
          max={10}
          step={1}
          value={nSystems}
          onChange={setNSystems}
          format={(value) => value.toFixed(0)}
        />
      </DemoWorkbenchCard>

      <DemoWorkbenchNotice
        tone={segalHolds ? "success" : "danger"}
        className="text-center"
      >
        <div
          className={`text-lg font-bold ${segalHolds ? "text-green-400" : "text-red-400"}`}
        >
          {segalHolds ? "✓ SEGAL VERIFIED" : "✗ SEGAL VIOLATED"}
        </div>
        <div className="text-gray-300 text-sm">
          Total interaction = {totalInteraction} | Pairwise sum = {pairwiseSum}
        </div>
      </DemoWorkbenchNotice>

      <DemoResponsiveGrid minColumnWidth={160} gap={12}>
        <DemoWorkbenchMetricCard
          label="Pairwise checks"
          value={pairs.length}
          sub={`O(n²) = O(${nSystems}²)`}
        />
        <DemoWorkbenchMetricCard
          label="Higher-order checks avoided"
          value={bruteForce}
          color="#f87171"
          sub={`O(2^n) would need ${Math.pow(2, nSystems) - 1}`}
        />
      </DemoResponsiveGrid>

      <DemoWorkbenchInset title="Pairwise budgets B(i,j)">
        <div className="grid grid-cols-5 gap-1 text-xs">
          {pairs.slice(0, 15).map((p, idx) => (
            <div
              key={idx}
              className={`p-1 rounded text-center ${
                p.B > 0
                  ? "bg-yellow-900/30 text-yellow-400"
                  : "bg-gray-700 text-gray-500"
              }`}
            >
              ({p.i},{p.j}): {p.B}
            </div>
          ))}
          {pairs.length > 15 && (
            <div className="text-gray-500 p-1">+{pairs.length - 15} more</div>
          )}
        </div>
      </DemoWorkbenchInset>
    </DemoWorkbenchStack>
  );
};

export default SegalVerificationPanel;
